import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';

import { AlertList, ErrorResponse, ModalCard, Spinner } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';
import { TableView } from '~components/table-view';

import { PAGE_SIZE } from '~constants/common';

import columns from '~support-at-home/forms/budgets/bulkCopyList';

import { useGetBulkCopyEstimateQuery } from '~support-at-home/actions/budgets';
import { getAll as getPricePlanRevisions } from '~support-at-home/actions/pricePlanRevisions';

interface BulkCopyFilters {
  date_range?: { gte?: string; lte?: string };
  client_ids?: number[];
  funding_type_ids?: number[];
  status_ids?: string[];
}

interface ComponentProps {
  value: BulkCopyFilters;
  onChange?: (name: string, value: any) => any;
}

const ListAndConfirm: React.FC<ComponentProps> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  const { date_range, client_ids, funding_type_ids, status_ids } = value;

  const { data, isFetching, error } = useGetBulkCopyEstimateQuery(
    {
      start_date: date_range?.gte || '',
      end_date: date_range?.lte || '',
      client_ids,
      funding_type_ids,
      status_ids,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !date_range?.gte || !date_range?.lte,
    },
  );

  const syncedRef = useRef<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Update budget_ids when budgets data changes
  useEffect(() => {
    if (!onChange || !data) return;

    const budgetIds = data.map((budget) => budget.id);
    const revisionIds = [...new Set(data.map((budget) => budget.price_plan_revision_id))];
    // Only re-sync when the actual budget set / revision spread changes — prevents a render loop
    // (onChange identity changes every Formik render).
    const signature = `${budgetIds.join(',')}|${revisionIds.join(',')}`;
    if (syncedRef.current === signature) return;
    syncedRef.current = signature;

    // The result set changed (new search) — go back to page 1 so pagination stays in bounds.
    setCurrentPage(1);

    onChange('budget_ids', budgetIds);

    // Default the target price plan to the budgets' current revision when every selected budget shares one.
    // NOTE: price_plan_id is NOT a column — read the plan id from the eager-loaded revision association.
    if (data.length > 0 && revisionIds.length === 1 && revisionIds[0]) {
      const planId = data[0].price_plan_revision?.plan_id;
      onChange('price_plan_id', planId ?? null);
      onChange('price_plan_revision_id', revisionIds[0]);
      if (planId) dispatch(getPricePlanRevisions({ plan_id: planId }));
    } else {
      onChange('price_plan_id', null);
      onChange('price_plan_revision_id', null);
    }
  }, [data, onChange]);

  if (isFetching) return <Spinner loading={isFetching} />;

  if (error) return <ErrorResponse message={getErrorMessage(error)} />;

  if (!data) return null;

  if (data.length === 0) {
    return (
      <AlertMessage
        id="no-budgets-alert"
        name="no-budgets-alert"
        value="No budgets found, please adjust search criteria and try again."
        type="alert-info"
        color="info"
      />
    );
  }

  // Client-side pagination — `data` already holds every matched budget (the full set is required
  // in Formik for the copy itself), so only the current page's slice is handed to TableView to
  // avoid rendering thousands of rows in the DOM.
  const pagedData = data.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div>
      <ModalCard header={`${data?.length} Found Budgets`} className="w-100 mb-3">
        {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss. A full page of rows
            still overflows the modal even with pagination. */}
        <div className="bulk-copy-scroll-table">
          <TableView
            id="bulk-copy-estimate-table"
            data={pagedData}
            columns={columns}
            readonly
            striped
            allowPagination
            paginationType="pages"
            showCount
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={data.length}
            onPageChange={setCurrentPage}
          />
        </div>
      </ModalCard>
      <AlertList
        header="Please Note! These copies will not account for:"
        color="warning"
        listItems={['Outdated price plans, client packages or funding types', 'Deleted or inactive clients']}
      />
    </div>
  );
};

export default ListAndConfirm;
